import React, { useState, useEffect } from 'react';
import {
  Box, Button, TextField, Typography,
  Container, Alert, CircularProgress
} from '@mui/material';
import { Visibility, VisibilityOff, LockReset } from '@mui/icons-material';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';

const API_URL = 'https://imara-production-91ab.up.railway.app';

const inputStyle = {
  '& .MuiOutlinedInput-root': {
    color: 'white',
    '& fieldset': { borderColor: 'rgba(255,255,255,0.2)' },
    '&:hover fieldset': { borderColor: '#4ade80' },
    '&.Mui-focused fieldset': { borderColor: '#4ade80' },
  },
  '& .MuiInputLabel-root': { color: 'rgba(255,255,255,0.5)' },
  '& .MuiInputLabel-root.Mui-focused': { color: '#4ade80' },
};

export default function ResetPassword() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');

  const [newPassword, setNewPassword]     = useState('');
  const [confirmPassword, setConfirm]     = useState('');
  const [showNew, setShowNew]             = useState(false);
  const [showConfirm, setShowConfirm]     = useState(false);
  const [loading, setLoading]             = useState(false);
  const [success, setSuccess]             = useState(false);
  const [error, setError]                 = useState('');

  useEffect(() => {
    if (!token) {
      setError('Invalid or missing reset token. Please request a new reset link.');
    }
  }, [token]);

  async function handleReset() {
    if (!newPassword) { setError('Please enter a new password.'); return; }
    if (newPassword.length < 6) { setError('Password must be at least 6 characters.'); return; }
    if (newPassword !== confirmPassword) { setError('Passwords do not match.'); return; }

    setLoading(true); setError('');
    try {
      await axios.post(`${API_URL}/auth/reset-password`, {
        token,
        new_password: newPassword,
      });
      setSuccess(true);
      // Auto redirect to login after 3 seconds
      setTimeout(() => navigate('/'), 3000);
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (detail?.includes('expired')) {
        setError('This reset link has expired. Please request a new one.');
      } else {
        setError(detail || 'Something went wrong. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  }

  return (
    <Box sx={{
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 50%, #0f3460 100%)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
    }}>
      <Container maxWidth="xs">
        <Box sx={{
          background: 'rgba(255,255,255,0.05)',
          backdropFilter: 'blur(10px)',
          borderRadius: 4,
          p: 4,
          border: '1px solid rgba(255,255,255,0.1)',
        }}>
          {/* Logo */}
          <Typography variant="h3" sx={{
            textAlign: 'center', color: '#4ade80',
            fontWeight: 900, mb: 0.5,
          }}>
            🌿 Imara
          </Typography>

          {!success ? (
            <>
              <Typography sx={{
                textAlign: 'center', color: 'rgba(255,255,255,0.5)',
                mb: 1, fontSize: '0.85rem',
              }}>
                Reset your password
              </Typography>
              <Typography sx={{
                textAlign: 'center', color: 'rgba(255,255,255,0.35)',
                mb: 4, fontSize: '0.78rem',
              }}>
                Enter your new password below.
              </Typography>

              {error && (
                <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>
              )}

              {/* New Password */}
              <Box sx={{ position: 'relative', mt: 2, mb: 1 }}>
                <TextField
                  fullWidth
                  label="New Password"
                  type={showNew ? 'text' : 'password'}
                  value={newPassword}
                  onChange={e => { setNewPassword(e.target.value); setError(''); }}
                  sx={inputStyle}
                />
                <Box
                  onClick={() => setShowNew(s => !s)}
                  sx={{
                    position: 'absolute', right: 14, top: '50%',
                    transform: 'translateY(-50%)',
                    cursor: 'pointer', color: '#7fa8c8',
                    display: 'flex', alignItems: 'center', zIndex: 10,
                    '&:hover': { color: '#4ade80' },
                  }}
                >
                  {showNew ? <VisibilityOff /> : <Visibility />}
                </Box>
              </Box>

              {/* Confirm Password */}
              <Box sx={{ position: 'relative', mt: 2, mb: 1 }}>
                <TextField
                  fullWidth
                  label="Confirm New Password"
                  type={showConfirm ? 'text' : 'password'}
                  value={confirmPassword}
                  onChange={e => { setConfirm(e.target.value); setError(''); }}
                  onKeyDown={e => e.key === 'Enter' && handleReset()}
                  sx={inputStyle}
                />
                <Box
                  onClick={() => setShowConfirm(s => !s)}
                  sx={{
                    position: 'absolute', right: 14, top: '50%',
                    transform: 'translateY(-50%)',
                    cursor: 'pointer', color: '#7fa8c8',
                    display: 'flex', alignItems: 'center', zIndex: 10,
                    '&:hover': { color: '#4ade80' },
                  }}
                >
                  {showConfirm ? <VisibilityOff /> : <Visibility />}
                </Box>
              </Box>

              {/* Password strength hint */}
              <Typography sx={{ fontSize: '0.72rem', color: 'rgba(255,255,255,0.3)', mt: 1 }}>
                Minimum 6 characters
              </Typography>

              <Button
                fullWidth
                variant="contained"
                onClick={handleReset}
                disabled={loading || !token}
                startIcon={loading ? null : <LockReset />}
                sx={{
                  mt: 3, py: 1.5,
                  background: 'linear-gradient(90deg, #4ade80, #22d3ee)',
                  color: '#000', fontWeight: 700, borderRadius: 2,
                  '&:hover': { background: 'linear-gradient(90deg, #22d3ee, #4ade80)' },
                  '&.Mui-disabled': { background: 'rgba(255,255,255,0.1)', color: '#555' },
                }}
              >
                {loading ? <CircularProgress size={24} /> : 'Reset Password'}
              </Button>
            </>
          ) : (
            /* Success state */
            <Box sx={{ textAlign: 'center', py: 2 }}>
              <Typography sx={{ fontSize: '3rem', mb: 2 }}>✅</Typography>
              <Typography sx={{
                color: '#4ade80', fontWeight: 700,
                fontSize: '1.1rem', mb: 1,
              }}>
                Password reset successfully!
              </Typography>
              <Typography sx={{
                color: 'rgba(255,255,255,0.5)',
                fontSize: '0.85rem', mb: 3, lineHeight: 1.6,
              }}>
                You can now log in with your new password.
                Redirecting to login in 3 seconds...
              </Typography>
              <Button
                onClick={() => navigate('/')}
                sx={{
                  background: 'linear-gradient(90deg, #4ade80, #22d3ee)',
                  color: '#000', fontWeight: 700,
                  textTransform: 'none', borderRadius: 2, px: 3,
                }}
              >
                Go to Login
              </Button>
            </Box>
          )}
        </Box>
      </Container>
    </Box>
  );
}